import { useTranslation } from 'react-i18next'

import { LOCAL_STORAGE_BIRDVIEW_KPIS_ORDER_KEY } from '../../../constants/storage'
import { getItemsIds } from '../../../utils/common'
import { BirdviewChartCard } from '../BirdviewChartCard'
import { BirdviewChartCatalog } from '../BirdviewChartCatalog'
import {
  BirdviewChartGrid,
  useBirdviewChartGridHidden,
} from '../BirdviewChartGrid'
import { BirdviewHeaderActionsPortal } from '../BirdviewHeader'
import { BirdviewSparkline } from '../BirdviewSparkline'
import { BirdviewTileRow } from '../BirdviewTileRow'
import { BirdviewSection } from '../DashboardBirdview.constants'
import { checkIsBirdviewTeamScope } from '../DashboardBirdview.utils'
import { getBirdviewStorageKey } from '../DashboardBirdview.utils.storage'
import { DashboardTile } from '../DashboardTile'

import { BIRDVIEW_ANALYTICS_TILES_PER_ROW } from './BirdviewAnalytics.constants'
import {
  getBirdviewAnalyticsCatalogRows,
  getBirdviewAnalyticsCharts,
  getBirdviewAnalyticsHiddenRows,
  getBirdviewAnalyticsHiddenStorageKey,
  getBirdviewAnalyticsKpis,
  getBirdviewAnalyticsSoonRows,
} from './BirdviewAnalytics.utils'

function BirdviewAnalytics({
  preset,
  scope,
  sections,
  storageScope,
  onNavigate,
  onResetLayout,
}) {
  const { t } = useTranslation()

  const kpis = getBirdviewAnalyticsKpis({
    preset,
    section: sections[BirdviewSection.Kpis],
    t,
  })
  const charts = getBirdviewAnalyticsCharts({
    isTeamScope: checkIsBirdviewTeamScope(scope),
    preset,
    sections,
    t,
  })
  const chartIds = getItemsIds(charts)

  const { hiddenIds, hideItem, showItem } = useBirdviewChartGridHidden({
    ids: chartIds,
    storageKey: getBirdviewAnalyticsHiddenStorageKey({
      ids: chartIds,
      storageScope,
    }),
  })

  const catalogRows = getBirdviewAnalyticsCatalogRows({
    charts,
    hiddenIds,
    t,
  })
  const hiddenRows = getBirdviewAnalyticsHiddenRows(catalogRows)

  function handleToggleChart(id, isShown) {
    if (isShown) {
      showItem(id)
    } else {
      hideItem(id)
    }
  }

  const catalogProps = {
    shownCount: catalogRows.length - hiddenRows.length,
    totalCount: catalogRows.length,
    onReset: onResetLayout,
    onToggle: handleToggleChart,
  }

  function renderKpi(kpi) {
    return (
      <DashboardTile
        dragHandle
        Chart={BirdviewSparkline}
        chartProps={{ points: kpi.points, tone: kpi.tone, unit: kpi.unit }}
        count={kpi.count}
        delta={kpi.delta}
        deltaTone={kpi.deltaTone}
        label={kpi.label}
        state={kpi.state}
      />
    )
  }

  function renderChart(chart, dragHandleProps, { onCycleSize }) {
    return (
      <BirdviewChartCard
        chart={chart}
        dragHandleProps={dragHandleProps}
        section={chart.section}
        title={chart.title}
        onCycleSize={onCycleSize}
        onNavigate={onNavigate}
        onRemove={hideItem}
      />
    )
  }

  return (
    <>
      <BirdviewHeaderActionsPortal>
        <BirdviewChartCatalog
          {...catalogProps}
          rows={[
            ...catalogRows,
            ...getBirdviewAnalyticsSoonRows({ preset, t }),
          ]}
        />
      </BirdviewHeaderActionsPortal>
      <BirdviewTileRow
        columns={BIRDVIEW_ANALYTICS_TILES_PER_ROW}
        items={kpis}
        renderItem={renderKpi}
        storageKeyPrefix={getBirdviewStorageKey(
          LOCAL_STORAGE_BIRDVIEW_KPIS_ORDER_KEY,
          [storageScope]
        )}
      />
      <BirdviewChartGrid
        catalogProps={{ ...catalogProps, rows: hiddenRows }}
        hiddenIds={hiddenIds}
        items={charts}
        renderItem={renderChart}
        storageScope={storageScope}
      />
    </>
  )
}

export default BirdviewAnalytics
